import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Detect browser
function getBrowserInfo() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';
  let deviceType = 'desktop';

  // Browser detection
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';

  // OS detection
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  // Device type detection
  if (/Mobi|Android/i.test(ua)) deviceType = 'mobile';
  else if (/Tablet|iPad/i.test(ua)) deviceType = 'tablet';

  return { browser, os, deviceType };
}

export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker and check existing subscription
  useEffect(() => {
    if (!isSupported) return;

    const checkSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        setSubscription(existingSubscription);
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };

    // Register service worker
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('Service Worker registered:', registration);
        checkSubscription();
      })
      .catch((err) => {
        console.error('Service Worker registration failed:', err);
      });
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        setError('Bildirim izni reddedildi');
        setIsLoading(false);
        return false;
      }

      // Get VAPID public key from server
      const vapidResponse = await api.get('/push/vapid-public-key');
      const vapidPublicKey = vapidResponse.data.publicKey;

      if (!vapidPublicKey) {
        throw new Error('VAPID public key not found');
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Get browser info
      const { browser, os, deviceType } = getBrowserInfo();

      // Send subscription to server
      const subscriptionJSON = pushSubscription.toJSON();
      await api.post('/push/subscribe', {
        endpoint: subscriptionJSON.endpoint,
        keys: subscriptionJSON.keys,
        browser,
        os,
        device_type: deviceType
      });

      setSubscription(pushSubscription);
      setIsLoading(false);
      return true;

    } catch (err) {
      console.error('Error subscribing to push:', err);
      setError(err.message || 'Bildirim aboneliği başarısız');
      setIsLoading(false);
      return false;
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return false;

    setIsLoading(true);
    setError(null);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Notify server
      await api.post('/push/unsubscribe', null, {
        params: { endpoint: subscription.endpoint }
      });

      setSubscription(null);
      setIsLoading(false);
      return true;

    } catch (err) {
      console.error('Error unsubscribing:', err);
      setError(err.message || 'Abonelik iptali başarısız');
      setIsLoading(false);
      return false;
    }
  }, [subscription]);

  return {
    permission,
    subscription,
    isSupported,
    isLoading,
    error,
    isSubscribed: !!subscription,
    subscribe,
    unsubscribe
  };
}

export default usePushNotifications;
